import React from 'react';

function formatFollowers(n) {
  if (n === null || n === undefined) return '—';
  if (n >= 1000000) return (n / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  return String(n);
}

function ClientCard({ client, stats, onDelete, onEdit, onGenerateReport, userRole }) {
  // Calculate token status
  const getTokenStatus = () => {
    if (!client.token_expires_at) return null;
    const today = new Date();
    const expiry = new Date(client.token_expires_at);
    const daysLeft = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    if (daysLeft <= 0) return { type: 'expired', daysLeft, label: 'Token expired — update needed' };
    if (daysLeft <= 14) return { type: 'warning', daysLeft, label: `Token expires in ${daysLeft} days` };
    return { type: 'healthy', daysLeft, label: null };
  };

  const tokenStatus = getTokenStatus();

  // Fixed: Just call onDelete with the client object
  const handleDelete = () => {
    onDelete(client);
  };

  return (
    <div
      className="client-card"
      style={{ '--card-stripe': client.brand_color_primary || '#E06A28' }}
    >
      <div className="card-header">
        <h3>{client.name}</h3>
      </div>

      {client.has_token && !client.instagram_id && !client.facebook_id && (
        <div className="token-banner warning">
          <div className="warning-pulse" />
          <strong>Action needed</strong>
          <div className="warning-tooltip-wrapper">
            <div className="warning-tooltip-icon">?</div>
            <div className="warning-tooltip-box">
              Your Meta account is connected but no Facebook Page has been selected for this client yet. Click "Select Facebook Page" to complete setup.
            </div>
          </div>
          <button onClick={() => onEdit(client, { autoConnect: true })} className="token-banner-btn">
            📘 Select Facebook Page
          </button>
        </div>
      )}

      {tokenStatus?.type === 'expired' && (
        <div className="token-banner expired">
          🔴 {tokenStatus.label}
          <button onClick={() => onEdit(client)} className="token-banner-btn">Update Token</button>
        </div>
      )}

      {tokenStatus?.type === 'warning' && (
        <div className="token-banner warning">
          🟡 {tokenStatus.label}
          <button onClick={() => onEdit(client)} className="token-banner-btn">Refresh Token</button>
        </div>
      )}

      <div className="card-body">
        <div className="platforms">
          {client.instagram_id && (
            client.instagram_url
              ? <a href={client.instagram_url} target="_blank" rel="noopener noreferrer" className="platform-badge instagram" style={{ textDecoration: 'none', cursor: 'pointer' }}>
                  📷 Instagram ↗
                </a>
              : <span className="platform-badge instagram">📷 Instagram</span>
          )}
          {client.facebook_id && (
            client.facebook_url
              ? <a href={client.facebook_url} target="_blank" rel="noopener noreferrer" className="platform-badge facebook" style={{ textDecoration: 'none', cursor: 'pointer' }}>
                  📘 Facebook ↗
                </a>
              : <span className="platform-badge facebook">📘 Facebook</span>
          )}
        </div>

        {/* Mini stats — only shown when client has at least one platform connected */}
        {stats && (client.instagram_id || client.facebook_id) && (
          <div className="mini-stats">
            <div className="mini-stat">
              <div className="mini-stat-val">{formatFollowers(stats.followers)}</div>
              <div className="mini-stat-lbl">Followers</div>
            </div>
            <div className="mini-stat">
              <div className="mini-stat-val">{stats.engagementRate !== null ? `${stats.engagementRate}%` : '—'}</div>
              <div className="mini-stat-lbl">Eng. Rate</div>
            </div>
            <div className="mini-stat">
              <div className="mini-stat-val">{stats.lastReport || '—'}</div>
              <div className="mini-stat-lbl">Last Report</div>
            </div>
          </div>
        )}

        {client.report_schedule && client.next_report_date && (() => {
          const next = new Date(client.next_report_date);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const isOverdue = next < today;
          const dateLabel = next.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' });
          return (
            <div className={`scheduled-banner ${isOverdue ? 'scheduled-overdue' : ''}`}>
              <span className="scheduled-text">📅 Next report scheduled</span>
              <span className="scheduled-date">{dateLabel}</span>
            </div>
          );
        })()}

        <div className="card-actions">
          <button
            onClick={() => onGenerateReport(client)}
            className="btn-primary"
          >
            📄 Generate Report
          </button>

          <div className="card-actions-row">
            <button
              onClick={() => onEdit(client)}
              className="btn-edit"
            >
              ✏️ Edit
            </button>

            <button
              onClick={handleDelete}
              className="btn-danger"
            >
              🗑️ Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ClientCard;
